import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
// emoji-mart 5 não traz mais CSS próprio nem exporta React: o componente vem
// do pacote @emoji-mart/react e os emojis do @emoji-mart/data.
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import {
  Paperclip,
  Smile,
  Send,
  X,
  Mic,
  XCircle,
  CheckCircle2,
  Loader2,
  Clock,
  FileText,
  Zap,
} from "lucide-react";

import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useThemeContext } from "../../context/DarkMode";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import MediaLibraryPicker from "../MediaLibraryPicker";
import FigurinhaPicker from "../FigurinhaPicker";
import EnviarTemplateModal from "../EnviarTemplateModal";
import AssistenteDeTexto from "./AssistenteDeTexto";
import PreviaDeAnexos from "./PreviaDeAnexos";
import EnviarLocalizacaoModal from "../EnviarLocalizacaoModal";
import CameraModal from "../CameraModal";
import EnviarContatoModal from "../EnviarContatoModal";
import BarraDeFormatacao, { aplicarMarca } from "./BarraDeFormatacao";
import MenuDeAcoes from "./MenuDeAcoes";
import { paraWav } from "./wav";
import { ACCEPT_UPLOAD, problemaDoArquivo } from "../../constants/uploads";

// Gravação de voz com o MediaRecorder do próprio navegador.
//
// Antes era a mic-recorder-to-mp3, que não funciona mais aqui: ela espera o
// codificador `lamejs` como variável global (formato UMD) e o bundler atual
// carrega tudo como módulo ES. O resultado era `ReferenceError: Lame is not
// defined` na primeira tentativa — o gravador nunca chegava a existir, e o
// botão de áudio simplesmente não fazia nada.
//
// O MediaRecorder é padrão do navegador, não tem dependência e grava em
// Opus — que é justamente o codec que o WhatsApp usa para mensagem de voz.
// O MP3 anterior era convertido de qualquer forma antes de sair.

// Formatos por ordem de preferência; o navegador escolhe o primeiro que
// souber gravar. Safari só tem mp4/aac, Chrome e Firefox preferem opus.
const FORMATOS = [
  { mime: "audio/ogg;codecs=opus", ext: "ogg" },
  { mime: "audio/webm;codecs=opus", ext: "webm" },
  { mime: "audio/mp4", ext: "m4a" },
  { mime: "audio/webm", ext: "webm" }
];

const escolherFormato = () => {
  if (typeof MediaRecorder === "undefined") return null;

  return (
    FORMATOS.find((f) => MediaRecorder.isTypeSupported(f.mime)) || {
      mime: "",
      ext: "webm"
    }
  );
};

const MessageInput = ({ ticketStatus, isGroup }) => {
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [figurinhasAberto, setFigurinhasAberto] = useState(false);
  // Todas as respostas prontas da empresa, para a faixa de atalhos acima do
  // campo. Antes elas só existiam para quem soubesse digitar "/": quem não
  // sabia do atalho nunca descobriu que a tela de respostas prontas servia
  // para alguma coisa.
  const [atalhos, setAtalhos] = useState([]);
  const [atalhosAbertos, setAtalhosAbertos] = useLocalStorage(
    "faixaDeAtalhos",
    true,
  );
  const [loading, setLoading] = useState(false);

  // Guarda a resposta pronta escolhida até a mensagem sair, para então mover
  // o cartão do cliente no quadro. Editar o texto antes de mandar não desfaz
  // a escolha: a resposta continua sendo aquela, com outras palavras.
  const [respostaAplicavel, setRespostaAplicavel] = useState(null);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const [arrastandoArquivo, setArrastandoArquivo] = useState(false);
  const [localizacaoAberta, setLocalizacaoAberta] = useState(false);
  const [cameraAberta, setCameraAberta] = useState(false);
  const [contatoAberto, setContatoAberto] = useState(false);
  const [formatacaoAberta, setFormatacaoAberta] = useLocalStorage(
    "barraDeFormatacao",
    false,
  );
  // Mensagem privada: fica na conversa para a equipe e não sai para o cliente.
  const [privado, setPrivado] = useState(false);
  // Participantes do grupo e quem foi marcado com @ nesta mensagem.
  const [participantes, setParticipantes] = useState([]);
  const [mencionados, setMencionados] = useState([]);
  const [listaDeMencao, setListaDeMencao] = useState(null);
  // Com que nome esta conversa vai assinada. `opcoes` são os nomes
  // alternativos da pessoa; `efetivo` é o que vale agora (o fixado nesta
  // conversa, o da fila, ou o nome do cadastro).
  const [assinatura, setAssinatura] = useState({
    efetivo: "",
    aliasId: null,
    opcoes: [],
  });
  const inputRef = useRef();
  // Gravação em curso: o MediaRecorder, o stream do microfone e os pedaços.
  const gravadorRef = useRef(null);
  const pedacosRef = useRef([]);
  const emojiBoxRef = useRef();
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const { darkMode } = useThemeContext();

  // Grupo nunca é "aceito" por ninguém — é o motivo de ele ter aba própria —,
  // então ele vive em "aguardando". Exigir `open` para digitar trancava o
  // editor inteiro em toda conversa de grupo: dava para ler e não para
  // responder. Encerrado continua bloqueado, em grupo como em qualquer outra.
  const conversaAberta =
    ticketStatus === "open" || (isGroup && ticketStatus === "pending");
  const disabled = loading || recording || !conversaAberta;

  // O nome que vai na assinatura. O alias vem do servidor já resolvido; o
  // nome do cadastro é a reserva enquanto a consulta não volta.
  const nomeDeAssinatura = assinatura.efetivo || user?.name || "";

  // A janela de 24 h do WhatsApp oficial. `aplica: false` nos demais canais —
  // ali nunca há prazo e a barra de digitar não pode sumir.
  const [janela, setJanela] = useState({ aplica: false, aberta: true });
  const [templateAberto, setTemplateAberto] = useState(false);

  const conferirJanela = useCallback(async () => {
    if (!ticketId) return;

    try {
      const { data } = await api.get(`/meta/templates/window/${ticketId}`);
      setJanela(data);
    } catch {
      // Sem resposta, o campo de texto continua: bloquear por falha de
      // consulta impediria de responder um canal que nem tem esse limite.
      setJanela({ aplica: false, aberta: true });
    }
  }, [ticketId]);

  useEffect(() => {
    conferirJanela();
  }, [conferirJanela]);

  // Em tela estreita o texto longo do placeholder é cortado no meio.
  const [placeholderCurto, setPlaceholderCurto] = useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );

  // Sair da tela no meio de uma gravação deixava o microfone aberto para
  // sempre — a luz do aparelho acesa e o aviso de "gravando" na aba.
  useEffect(() => {
    return () => {
      const atual = gravadorRef.current;
      if (!atual) return;
      try {
        if (atual.recorder.state !== "inactive") atual.recorder.stop();
      } catch {
        // Recorder já encerrado por outro caminho.
      }
      atual.stream.getTracks().forEach((trilha) => trilha.stop());
      gravadorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const aoRedimensionar = () => setPlaceholderCurto(window.innerWidth < 640);
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, []);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  useEffect(() => {
    inputRef.current.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    if (!showEmoji) return;
    const handleClickAway = (e) => {
      if (emojiBoxRef.current && !emojiBoxRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [showEmoji]);

  // Nome de assinatura desta conversa. Sem isto, a mensagem sairia sempre com
  // o nome do cadastro e os aliases não serviriam para nada.
  useEffect(() => {
    if (!ticketId) return undefined;

    const ativo = { current: true };

    api
      .get(`/user-aliases/ticket/${ticketId}`)
      .then(({ data }) => {
        if (ativo.current && data) setAssinatura(data);
      })
      .catch(() => undefined);

    return () => {
      ativo.current = false;
    };
  }, [ticketId]);

  // Participantes do grupo — alimentam a lista do "@". Em conversa individual
  // a rota devolve vazio e nada disso aparece.
  useEffect(() => {
    if (!ticketId) return undefined;

    const ativo = { current: true };

    api
      .get(`/tickets/${ticketId}/participantes`)
      .then(({ data }) => {
        if (ativo.current) setParticipantes(Array.isArray(data) ? data : []);
      })
      .catch(() => undefined);

    return () => {
      ativo.current = false;
    };
  }, [ticketId]);

  // O copiloto (no cabeçalho do ticket) manda o texto por evento, para não
  // precisar levantar o estado da caixa de mensagem até lá.
  useEffect(() => {
    const aoReceber = (evento) => {
      setInputMessage(evento.detail || "");
      inputRef.current?.focus();
    };

    document.addEventListener("copilot:usar-texto", aoReceber);
    return () => document.removeEventListener("copilot:usar-texto", aoReceber);
  }, []);

  // Carrega a lista inteira uma vez: são poucas dezenas de respostas, e a
  // faixa não pode mostrar meia lista. O serviço pagina de 20 em 20.
  useEffect(() => {
    let ativo = true;

    (async () => {
      try {
        const todas = [];
        let pagina = 1;
        let temMais = true;

        // Cinco páginas (100 respostas) é o teto: além disso a faixa deixa de
        // ser um atalho e vira uma lista, e a tela de respostas prontas é o
        // lugar dessa lista.
        while (temMais && pagina <= 5) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.get("/quickAnswers/", {
            params: { pageNumber: pagina },
          });

          todas.push(...(data?.quickAnswers || []));
          temMais = Boolean(data?.hasMore);
          pagina += 1;
        }

        if (ativo) setAtalhos(todas);
      } catch (err) {
        // A faixa é um extra: sem ela o "/" continua funcionando. Errar aqui
        // não pode encher a tela de aviso vermelho a cada atendimento aberto.
        if (ativo) setAtalhos([]);
      }
    })();

    return () => {
      ativo = false;
    };
  }, []);

  const handleChangeInput = (e) => {
    const valor = e.target.value;

    setInputMessage(valor);
    handleLoadQuickAnswer(valor);

    // "@" abre a lista de quem está no grupo, e o que vem depois filtra.
    // Mesma ideia do "/" das respostas prontas, que já existe aqui.
    if (participantes.length) {
      const ateOCursor = valor.slice(0, e.target.selectionStart ?? valor.length);
      const arroba = /(?:^|\s)@([^\s@]*)$/.exec(ateOCursor);

      setListaDeMencao(arroba ? arroba[1].toLowerCase() : null);
    }

    // Apagou tudo: desistiu daquela resposta. Sem isto, escrever outra coisa
    // do zero ainda moveria o card da resposta escolhida antes.
    if (!valor) setRespostaAplicavel(null);
  };

  // Troca o "@parcial" pelo número do participante e guarda o jid: é a lista de
  // jids que faz o WhatsApp avisar quem foi marcado — o texto sozinho não marca
  // ninguém.
  const escolherMencao = (participante) => {
    const campo = inputRef.current;
    const cursor = campo?.selectionStart ?? inputMessage.length;
    const antes = inputMessage.slice(0, cursor);
    const depois = inputMessage.slice(cursor);

    const novoAntes = antes.replace(/@([^\s@]*)$/, `@${participante.numero} `);
    const novo = novoAntes + depois;

    setInputMessage(novo);
    setListaDeMencao(null);
    setMencionados((anteriores) =>
      anteriores.includes(participante.jid)
        ? anteriores
        : [...anteriores, participante.jid],
    );

    requestAnimationFrame(() => {
      campo?.focus();
      campo?.setSelectionRange(novoAntes.length, novoAntes.length);
    });
  };

  const aplicarMovimentoDaResposta = async () => {
    if (!respostaAplicavel) return;

    const { id } = respostaAplicavel;
    setRespostaAplicavel(null);

    try {
      const { data } = await api.post(`/quickAnswers/${id}/aplicar`, {
        ticketId,
      });

      // Mover em silêncio deixa a pessoa sem saber se funcionou — e não
      // mover em silêncio é pior ainda: parece defeito. Cada caso fala.
      if (data?.cartao) {
        toast.success(
          data.cartao.criado
            ? `Cartão aberto em "${data.cartao.coluna}".`
            : `Cartão movido para "${data.cartao.coluna}".`
        );
      } else if (data?.semEfeito === "ja-na-coluna") {
        toast.info(`O cartão já estava em "${data.coluna}".`);
      } else if (data?.semEfeito === "sem-vinculo") {
        toast.info(
          "Esta resposta pronta não está ligada a nenhuma coluna do Kanban."
        );
      }
    } catch (err) {
      // A mensagem já chegou ao cliente; o cartão é que não andou.
      toast.warn("A mensagem foi enviada, mas não consegui mover o cartão.");
    }
  };

  const handleQuickAnswersClick = async (resposta) => {
    setTypeBar(false);
    setInputMessage(resposta.message || "");

    setRespostaAplicavel(resposta.moveKanbanColumnId ? resposta : null);

    if (!resposta.mediaUrl) return;

    // Traz o arquivo salvo e o põe como anexo pendente, igual a quem escolhe
    // um arquivo do computador. Nada sai sozinho: a pessoa ainda revisa e
    // clica em enviar, que é a promessa da tela de respostas prontas.
    try {
      setLoading(true);
      const resposta_http = await fetch(resposta.mediaUrl);

      if (!resposta_http.ok) throw new Error("Arquivo não encontrado");

      const blob = await resposta_http.blob();
      const nome =
        resposta.mediaName || resposta.mediaUrl.split("/").pop() || "arquivo";

      setMedias([new File([blob], nome, { type: blob.type })]);
    } catch (err) {
      toast.error(
        "Não consegui carregar o arquivo desta resposta. O texto foi preenchido."
      );
    }
    setLoading(false);
  };

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  };

  // Filtra o que o backend recusaria, dizendo o motivo por arquivo. Sem isto
  // o upload inteiro subia para só então voltar com um erro genérico.
  const aceitarArquivos = (arquivos) => {
    const bons = [];

    arquivos.forEach((arquivo) => {
      const problema = problemaDoArquivo(arquivo);

      if (problema) toast.error(`${arquivo.name}: ${problema}.`);
      else bons.push(arquivo);
    });

    return bons;
  };

  // Anexar de novo com arquivos já escolhidos ACRESCENTA, não troca: quem
  // seleciona três fotos e lembra da quarta não deveria perder as três. O
  // mesmo arquivo duas vezes (mesmo nome e tamanho) entra uma vez só.
  const adicionarMedias = (arquivos) => {
    const escolhidos = aceitarArquivos(arquivos);
    if (!escolhidos.length) return;

    setMedias((atuais) => {
      const jaTem = new Set(atuais.map((a) => `${a.name}:${a.size}`));
      const novos = escolhidos.filter((a) => !jaTem.has(`${a.name}:${a.size}`));
      return [...atuais, ...novos];
    });
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }

    adicionarMedias(Array.from(e.target.files));

    // Zera o input: escolher o mesmo arquivo de novo, depois de uma recusa,
    // não dispara `change` se o valor continuar lá.
    e.target.value = "";
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      adicionarMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async (e) => {
    setLoading(true);
    e.preventDefault();

    const legenda = inputMessage.trim();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach((media) => formData.append("medias", media));
    // `body` é a legenda opcional — vai uma só, para o conjunto de arquivos.
    if (legenda) {
      formData.append(
        "body",
        signMessage ? `*${nomeDeAssinatura}:*\n${legenda}` : legenda
      );
    }

    try {
      await api.post(`/messages/${ticketId}`, formData);
      setMedias([]);
      setInputMessage("");
      aplicarMovimentoDaResposta();
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
  };

  const handleSendMessage = async () => {
    const textoEnviado = inputMessage.trim();
    if (textoEnviado === "") return;

    // O botão não rouba o foco (veja `onPointerDown`) e o textarea continua
    // habilitado durante a requisição. Assim o teclado virtual permanece
    // aberto e a próxima frase já pode ser digitada.
    inputRef.current?.focus();

    // Libera o campo imediatamente para a próxima frase. Se a requisição
    // falhar, o texto volta apenas quando a pessoa ainda não começou outra
    // mensagem — assim uma resposta lenta nunca apaga o que ela digitou.
    setInputMessage("");
    setShowEmoji(false);
    setTypeBar(false);
    setMencionados([]);

    setLoading(true);

    // Privado não sai para o cliente: é a nota da equipe, gravada na conversa
    // por uma rota que nem chega a falar com o WhatsApp.
    if (privado) {
      try {
        await api.post(`/messages/${ticketId}/internal`, {
          body: textoEnviado,
        });
      } catch (err) {
        setInputMessage((textoAtual) => textoAtual || textoEnviado);
        toastError(err);
      }

      setLoading(false);
      setReplyingMessage(null);
      return;
    }

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? `*${nomeDeAssinatura}:*\n${textoEnviado}`
        : textoEnviado,
      quotedMsg: replyingMessage,
      // Só quem continua escrito no texto segue marcado: apagar o "@fulano" e
      // mandar assim mesmo não pode avisar o fulano.
      mentions: mencionados.filter((jid) =>
        textoEnviado.includes(String(jid).replace(/@.*$/, "")),
      ),
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
      aplicarMovimentoDaResposta();
    } catch (err) {
      setInputMessage((textoAtual) => textoAtual || textoEnviado);
      toastError(err);
    }

    setLoading(false);
    setReplyingMessage(null);
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          // `value` é o texto recém-digitado; `inputMessage` ainda tem o valor
          // anterior neste ponto do render e faria a busca ficar um passo atrás.
          params: { searchParam: value.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        setTypeBar(data.quickAnswers.length > 0);
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      // Sem HTTPS o navegador nem expõe o microfone. Acontece direto em
      // produção servida por HTTP, e o erro que aparecia era um
      // "undefined is not a function" que não dizia nada.
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error(
          "O navegador só libera o microfone em conexão segura (https). " +
            "Peça para quem cuida do servidor habilitar o certificado."
        );
        setLoading(false);
        return;
      }

      const formato = escolherFormato();

      if (!formato) {
        toast.error("Este navegador não sabe gravar áudio.");
        setLoading(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const recorder = new MediaRecorder(
        stream,
        formato.mime ? { mimeType: formato.mime } : undefined
      );

      pedacosRef.current = [];
      // Guarda o stream junto: parar o recorder não desliga o microfone, e
      // sem fechar as trilhas a luz do aparelho fica acesa depois da gravação.
      gravadorRef.current = { recorder, stream, ext: formato.ext };

      recorder.ondataavailable = (evento) => {
        if (evento.data && evento.data.size > 0) {
          pedacosRef.current.push(evento.data);
        }
      };

      // Um pedaço por segundo: sem o intervalo, o navegador só entrega tudo
      // no fim, e uma aba fechada no meio levaria a gravação inteira.
      recorder.start(1000);

      setRecording(true);
      setLoading(false);
    } catch (err) {
      // Permissão negada é o caso mais comum e não é defeito do sistema:
      // merece instrução, não a mensagem técnica do navegador.
      if (err?.name === "NotAllowedError") {
        toast.error(
          "Você bloqueou o microfone para este site. Libere no cadeado ao " +
            "lado do endereço e tente de novo."
        );
      } else if (err?.name === "NotFoundError") {
        toast.error("Nenhum microfone encontrado neste computador.");
      } else {
        toastError(err);
      }
      setLoading(false);
    }
  };

  // Encerra a gravação e devolve o áudio. Fecha o microfone em qualquer
  // caso — inclusive quando a pessoa desiste.
  const encerrarGravacao = () =>
    new Promise((resolve) => {
      const atual = gravadorRef.current;

      if (!atual) {
        resolve(null);
        return;
      }

      const finalizar = () => {
        atual.stream.getTracks().forEach((trilha) => trilha.stop());
        gravadorRef.current = null;

        const blob = new Blob(pedacosRef.current, {
          type: atual.recorder.mimeType || "audio/webm"
        });
        pedacosRef.current = [];

        resolve({ blob, ext: atual.ext });
      };

      if (atual.recorder.state === "inactive") {
        finalizar();
        return;
      }

      atual.recorder.onstop = finalizar;
      atual.recorder.stop();
    });

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const gravado = await encerrarGravacao();

      if (!gravado) {
        setRecording(false);
        setLoading(false);
        return;
      }

      // Áudio curto demais era descartado em silêncio: a pessoa gravava,
      // soltava, e simplesmente não acontecia nada — parecia microfone
      // quebrado. Agora ela sabe por que não foi.
      if (gravado.blob.size < 2000) {
        toast.info("Gravação muito curta. Segure o botão enquanto fala.");
        setLoading(false);
        setRecording(false);
        return;
      }

      // Converte para WAV antes de mandar: o formato do MediaRecorder não
      // declara a duração, e a biblioteca do WhatsApp precisa dela para
      // montar a mensagem de voz.
      const wav = await paraWav(gravado.blob);

      const formData = new FormData();
      const filename = `${new Date().getTime()}.wav`;
      formData.append("medias", wav, filename);
      // Sem legenda: mandar o nome do arquivo aqui fazia o áudio chegar no
      // celular do cliente com "1785342981.mp3" escrito embaixo, e a lista de
      // atendimentos mostrar isso como prévia da conversa.
      formData.append("body", "");
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    await encerrarGravacao();
    setRecording(false);
  };

  const renderReplyingMessage = (message) => {
    return (
      <div className="flex w-full items-center gap-2 px-3 pt-2">
        <div
          className={cn(
            "flex flex-1 overflow-hidden rounded-lg",
            !message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-black/5 dark:bg-white/10"
          )}
        >
          <span
            className={cn("w-1 shrink-0", !message.fromMe ? "bg-emerald-400" : "bg-sky-400")}
          />
          <div className="min-w-0 flex-1 whitespace-pre-wrap p-2.5 text-sm">
            {!message.fromMe && (
              <span className="flex font-medium text-sky-500">
                {message.contact?.name}
              </span>
            )}
            <span className="line-clamp-2">{message.body}</span>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Cancelar resposta"
          title="Cancelar resposta"
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => setReplyingMessage(null)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  // Troca o nome de assinatura DESTA conversa. Fica no ticket, não na tela:
  // quem assumir depois continua de onde o cliente parou, e o alias não volta
  // ao anterior a cada F5.
  const trocarAlias = async (aliasId) => {
    try {
      const { data } = await api.put(`/user-aliases/ticket/${ticketId}`, {
        aliasId,
      });

      if (data) {
        const { data: atual } = await api.get(
          `/user-aliases/ticket/${ticketId}`,
        );
        setAssinatura(atual);
      }
    } catch (err) {
      toastError(err);
    }
  };

  // A prévia dos anexos é uma janela por cima da conversa: dá para ver todos
  // os arquivos escolhidos, grandes, antes de mandar.
  const previaDeAnexos = medias.length > 0 && (
    <PreviaDeAnexos
      arquivos={medias}
      aoMudar={setMedias}
      aoAdicionar={adicionarMedias}
      legenda={inputMessage}
      aoMudarLegenda={setInputMessage}
      aoEnviar={handleUploadMedia}
      aoCancelar={() => {
        setMedias([]);
        setInputMessage("");
      }}
      enviando={loading}
    />
  );

  // Janela de 24 h fechada: texto livre não sai, e insistir gasta o erro
  // 131047 da Meta depois de a mensagem já aparecer como enviada aqui. O
  // caminho passa a ser o modelo aprovado, e a barra some para deixar isso
  // claro em vez de deixar alguém digitar no vazio.
  if (janela.aplica && !janela.aberta && conversaAberta) {
    return (
      <div className="flex flex-none flex-col gap-2 border-t bg-amber-500/10 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="flex items-start gap-2 text-sm">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p>
            Passaram-se mais de 24 h desde a última mensagem deste cliente. O
            WhatsApp só aceita modelo aprovado agora — enviar um reabre a
            conversa por mais 24 h.
          </p>
        </div>

        <Button
          className="self-start"
          onClick={() => setTemplateAberto(true)}
          disabled={loading}
        >
          <FileText className="h-4 w-4" />
          Enviar modelo
        </Button>

        <EnviarTemplateModal
          open={templateAberto}
          ticketId={ticketId}
          onClose={() => setTemplateAberto(false)}
          onEnviado={conferirJanela}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        // `@container` continua aqui porque quem decide o desenho é a largura
        // DESTA barra, não a da janela: um painel de conversa de 796px dentro
        // de uma tela de 1440px é estreito, e o breakpoint de viewport (`sm:`)
        // não sabe disso.
        "@container relative flex flex-none flex-col border-t bg-muted/40 pb-[env(safe-area-inset-bottom)]",
        arrastandoArquivo && "bg-primary/10 ring-1 ring-inset ring-primary/40",
      )}
      // Arrastar o arquivo do computador para a barra anexa direto — de lá
      // ele cai na prévia, igual ao que veio pelo clipe.
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setArrastandoArquivo(true);
      }}
      onDragLeave={() => setArrastandoArquivo(false)}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setArrastandoArquivo(false);
        const soltos = Array.from(e.dataTransfer?.files || []);
        if (soltos.length) adicionarMedias(soltos);
      }}
    >
      {previaDeAnexos}

      {/* A foto ou o vídeo da câmera entram na prévia dos anexos, como
          qualquer arquivo escolhido: dá para conferir e escrever legenda. */}
      {contatoAberto && (
        <EnviarContatoModal
          aberto
          aoFechar={() => setContatoAberto(false)}
          ticketId={ticketId}
        />
      )}

      {cameraAberta && (
        <CameraModal
          aberto
          aoFechar={() => setCameraAberta(false)}
          aoCapturar={(arquivo) => adicionarMedias([arquivo])}
        />
      )}

      {/* Montado só quando abre: assim cada envio começa com os campos
          limpos, sem precisar de efeito para zerar o formulário. */}
      {localizacaoAberta && (
        <EnviarLocalizacaoModal
          aberto
          aoFechar={() => setLocalizacaoAberta(false)}
          ticketId={ticketId}
        />
      )}

      <MediaLibraryPicker
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        ticketId={ticketId}
      />

      <EnviarTemplateModal
        open={templateAberto}
        ticketId={ticketId}
        onClose={() => setTemplateAberto(false)}
        onEnviado={conferirJanela}
      />

      {/* Fora das barras desktop/mobile: assim o seletor funciona nos dois. */}
      {showEmoji && (
        <div
          ref={emojiBoxRef}
          className="absolute bottom-full left-2 right-2 z-20 mb-2 flex justify-center sm:right-auto sm:justify-start"
        >
          <div className="max-w-full overflow-x-auto rounded-lg shadow-lg">
            <Picker
              data={emojiData}
              locale="pt"
              perLine={8}
              previewPosition="none"
              skinTonePosition="none"
              theme={darkMode ? "dark" : "light"}
              onEmojiSelect={handleAddEmoji}
            />
          </div>
        </div>
      )}
      <FigurinhaPicker
        aberto={figurinhasAberto}
        aoFechar={() => setFigurinhasAberto(false)}
        ticketId={ticketId}
        desabilitado={disabled}
      />

      {replyingMessage && renderReplyingMessage(replyingMessage)}

      {/* Faixa de atalhos: as respostas prontas à mão, sem precisar saber que
          existe um "/". Só o atalho aparece — o texto inteiro de vinte
          respostas não caberia, e é pelo atalho que a equipe as conhece.
          Clicar preenche o campo; enviar continua sendo decisão de quem
          atende, que é a promessa da tela de respostas prontas. */}
      {conversaAberta && atalhos.length > 0 && (
        <div className="flex items-center gap-1 border-b px-2 py-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label={
              atalhosAbertos ? "Esconder respostas prontas" : "Mostrar respostas prontas"
            }
            title={
              atalhosAbertos ? "Esconder respostas prontas" : "Mostrar respostas prontas"
            }
            aria-expanded={atalhosAbertos}
            onClick={() => setAtalhosAbertos((atual) => !atual)}
          >
            <Zap
              className={cn(
                "h-4 w-4",
                atalhosAbertos ? "text-primary" : "text-muted-foreground",
              )}
            />
          </Button>

          {atalhosAbertos ? (
            <div className="flex flex-1 gap-1 overflow-x-auto scrollbar-none">
              {atalhos.map((resposta) => (
                <button
                  key={resposta.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleQuickAnswersClick(resposta)}
                  // O texto inteiro no title: é o que evita mandar a resposta
                  // errada só porque dois atalhos começam igual.
                  title={resposta.message || resposta.mediaName || resposta.shortcut}
                  className="flex shrink-0 items-center gap-1 rounded-full border bg-background px-3 py-1 text-xs font-medium hover:bg-accent disabled:opacity-50"
                >
                  {resposta.mediaUrl && (
                    <Paperclip className="h-3 w-3 text-muted-foreground" />
                  )}
                  {resposta.shortcut}
                </button>
              ))}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">
              {atalhos.length} resposta{atalhos.length > 1 ? "s" : ""} pronta
              {atalhos.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      {formatacaoAberta && (
        <BarraDeFormatacao
          campoRef={inputRef}
          texto={inputMessage}
          aoMudar={setInputMessage}
          desabilitado={disabled}
        />
      )}

      {/* Uma faixa arredondada com tudo dentro: o "+" e o emoji abrem a
          barra, o texto no meio, e as ações de envio à direita. A fita de nove ícones saiu — em painel largo ela
          tomava 662px e espremia o campo, e abaixo de 1024px virava um "⋮"
          com a lista escrita de novo, à parte. O que ela tinha está no "+",
          igual em qualquer largura. */}
      <div className="flex w-full p-2">
        <div
          className={cn(
            "flex min-w-0 flex-1 items-end gap-1 rounded-[26px] border bg-background p-1.5 shadow-xs transition-colors",
            "focus-within:ring-1 focus-within:ring-ring",
            privado &&
              "border-amber-400 bg-amber-50 focus-within:ring-amber-400 dark:bg-amber-950/30",
          )}
        >
          <MenuDeAcoes
            desabilitado={disabled}
            idDoCampoDeArquivo="upload-button"
            aoAbrirBiblioteca={() => setLibraryOpen(true)}
            aoAbrirFigurinhas={() => setFigurinhasAberto((v) => !v)}
            aoAbrirCamera={() => setCameraAberta(true)}
            aoAbrirContato={() => setContatoAberto(true)}
            aoAbrirLocalizacao={() => setLocalizacaoAberta(true)}
            formatacaoAberta={formatacaoAberta}
            aoAlternarFormatacao={() => setFormatacaoAberta((v) => !v)}
            privado={privado}
            aoAlternarPrivado={() => setPrivado((v) => !v)}
            signMessage={signMessage}
            aoAlternarAssinatura={setSignMessage}
            nomeDeAssinatura={nomeDeAssinatura}
            opcoesDeAlias={assinatura.opcoes}
            aliasId={assinatura.aliasId}
            aoTrocarAlias={trocarAlias}
            nomeDoCadastro={user?.name}
          />

          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Emoji"
            className="h-9 w-9 shrink-0 rounded-full"
            onClick={() => setShowEmoji((prev) => !prev)}
          >
            <Smile className="h-5 w-5 text-muted-foreground" />
          </Button>

          {/* Fica aqui, escondido, porque o item "Anexo" do "+" dispara este
              input pelo id — e um input fora da árvore não abriria nada. */}
          <input
            multiple
            type="file"
            id="upload-button"
            className="hidden"
            disabled={disabled}
            // O `accept` filtra o que aparece no seletor do sistema; a
            // validação acima é o que realmente barra, porque o accept é
            // uma sugestão e o usuário pode ignorá-la.
            accept={ACCEPT_UPLOAD}
            onChange={handleChangeMedias}
          />

          {/* `min-w-0`: sem isto o campo não encolhe abaixo do próprio
              conteúdo e empurra os botões para fora da barra. */}
          <div className="relative min-w-0 flex-1">
            <textarea
              ref={inputRef}
              rows={1}
              placeholder={
                !conversaAberta
                  ? i18n.t("messagesInput.placeholderClosed")
                  : privado
                  ? "Mensagem privada — o cliente não vê"
                  : placeholderCurto
                  ? "Mensagem"
                  : i18n.t("messagesInput.placeholderOpen")
              }
              value={inputMessage}
              onChange={handleChangeInput}
              // Enviar não desabilita o campo: desabilitar um textarea focado
              // fecha o teclado virtual. A pessoa pode começar a próxima
              // mensagem enquanto a anterior termina de subir.
              disabled={recording || !conversaAberta}
              onPaste={(e) => {
                conversaAberta && handleInputPaste(e);
              }}
              onKeyPress={(e) => {
                if (loading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              onKeyDown={(e) => {
                // Atalhos de formatação, os mesmos de qualquer editor.
                if (!(e.ctrlKey || e.metaKey)) return;

                const marca =
                  e.key.toLowerCase() === "b"
                    ? "*"
                    : e.key.toLowerCase() === "i"
                      ? "_"
                      : "";

                if (!marca) return;

                e.preventDefault();
                aplicarMarca(inputRef.current, inputMessage, marca, setInputMessage);
              }}
              // Sem borda e sem fundo próprios: a moldura agora é a faixa
              // inteira, e um retângulo dentro dela quebraria o desenho.
              className="block max-h-32 w-full resize-none overflow-y-auto border-0 bg-transparent px-2 py-2 text-sm leading-5 shadow-none scrollbar-none placeholder:text-muted-foreground focus:outline-hidden focus-visible:outline-hidden focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-60 [&::-webkit-scrollbar]:hidden"
            />
            {/* Lista do "@": quem está no grupo, filtrado pelo que já foi
                digitado depois da arroba. */}
            {listaDeMencao !== null && (
              <ul className="absolute bottom-full left-0 mb-3 max-h-56 w-full overflow-y-auto rounded-lg border bg-popover shadow-md">
                {participantes
                  .filter(
                    (p) =>
                      !listaDeMencao ||
                      p.nome.toLowerCase().includes(listaDeMencao) ||
                      p.numero.includes(listaDeMencao),
                  )
                  .slice(0, 30)
                  .map((p) => (
                    <li key={p.jid} className="list-none">
                      <button
                        type="button"
                        onClick={() => escolherMencao(p)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                      >
                        <span className="min-w-0">
                          <span className="block truncate">{p.nome}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {p.numero}
                          </span>
                        </span>
                        {p.admin && (
                          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
                            admin
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
              </ul>
            )}

            {typeBar && (
              <ul className="absolute bottom-full left-0 mb-3 w-full overflow-hidden rounded-lg border bg-popover shadow-md">
                {quickAnswers.map((value, index) => (
                  <li key={index} className="list-none">
                    <button
                      type="button"
                      onClick={() => handleQuickAnswersClick(value)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                    >
                      {/* Clipe avisa que vem arquivo junto: sem isso a pessoa
                          escolhe achando que é só texto e leva um anexo. */}
                      {value.mediaUrl && (
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate">
                        {value.message
                          ? `${value.shortcut} - ${value.message}`
                          : `${value.shortcut} - ${value.mediaName || "arquivo"}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-0.5">
            {/* Correção e sugestão ficam coladas no campo, e não num painel à
                parte: é aqui que a pessoa está escrevendo quando precisa. */}
            <AssistenteDeTexto
              ticketId={ticketId}
              texto={inputMessage}
              aoTrocarTexto={setInputMessage}
              desabilitado={recording || loading || !conversaAberta}
            />

            {recording ? (
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-full"
                  disabled={loading}
                  onClick={handleCancelAudio}
                  aria-label="Descartar áudio"
                >
                  <XCircle className="h-5 w-5 text-destructive" />
                </Button>
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                ) : (
                  <RecordingTimer />
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-full"
                  onClick={handleUploadAudio}
                  disabled={loading}
                  aria-label="Enviar áudio"
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                </Button>
              </div>
            ) : (
              <>
                <span className="mx-1 h-5 w-px bg-border" />

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-full"
                  disabled={loading || !conversaAberta}
                  onClick={handleStartRecording}
                  aria-label="Gravar áudio"
                >
                  <Mic className="h-5 w-5 text-muted-foreground" />
                </Button>

                <Button
                  size="icon"
                  className="h-9 w-9 shrink-0 rounded-full"
                  aria-label="Enviar mensagem"
                  // Impede o botão de tirar o foco do textarea. Sem isto o
                  // blur acontece antes do clique e o teclado do celular já
                  // começa a fechar — o focus() do handler chegaria tarde.
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={handleSendMessage}
                  disabled={loading || !inputMessage.trim()}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageInput;
